import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import moment from 'moment'
import Head from 'next/head'
import Router from 'next/router'
import * as R from 'ramda'
import { toJS } from 'mobx'

import api from 'api'
import {
  COUPON_PROJECT,
  COUPON_TYPE,
  COUPON_REDEEMABLE_STATUS,
} from 'constants/coupon'
import * as skuHelper from 'helpers/skuHelper'
import useBreakpoint from 'hooks/useBreakpoint'

import { useUserAgentStore } from 'store/userAgentStore'
import { useErrorStore } from 'store/errorStore'
import { useLoadingStore } from 'store/loadingStore'
import { useShareStore } from 'store/shareStore'
import { useUserStore } from 'store/userStore'
import { useTrackingStore } from 'store/trackingStore'

import { COUPON_SCREEN_NAME, EVENT_ACTION, BUTTON_NAME } from 'tracking'
import * as errorUtil from 'utils/errorUtil'
import { formatCashNumber } from 'utils/formatUtil'
import { composeShareContentURL, permaLinkHashQueryString } from 'utils/urlUtil'
import BaseInfo from './BaseInfo'
import SkuButton from './SkuButton'

import DetailLoader from 'components/ContentLoaderV2/DetailLoader'
import DetailCard, { SkuInfoRow } from 'components/DetailCard'
import FixedWrap from 'components/FixedPortal'
import MerchantSkus from 'components/MerchantSkus'
import SuccessSnack from 'components/UI/SuccessSnack'
import BuySkuAmount from './BuySkuAmount'

const SkuDetail = observer(
  ({ asPath, sku, isFullPage, error, errorCallback, pathname, fastShare }) => {
    const pageUrl = composeShareContentURL(asPath)
    const [coupon, setCoupon] = useState(null)
    const [isFetchingProfile, setIsFetchingProfile] = useState(true)
    const [redeemProfile, setRedeemProfile] = useState({
      userPoints: 0,
      redeemableStatus: COUPON_REDEEMABLE_STATUS.DEFAULT,
    })
    const [openSnack, setOpenSanck] = useState(false)

    const userAgentStore = useUserAgentStore()
    const featureFlags = userAgentStore.featureFlags || {}
    const paymentIntegration = toJS(featureFlags.paymentIntegration)

    const {
      accessToken,
      isLoggedIn,
      hasBindPhone,
      checkingLoginStatus,
    } = useUserStore()
    const { trackEvent, isInited } = useTrackingStore()
    const { setSharablePayload } = useShareStore()
    const loadingStore = useLoadingStore()
    const { isMobileBreakpoint, matchesS } = useBreakpoint()
    const {
      wrapErrorHandler,
      handleAjaxError,
      openErrorModalV2,
    } = useErrorStore()

    const skuId = R.prop('id', sku)
    const loading = !sku

    const getRedeemProfile = useCallback(async () => {
      wrapErrorHandler(async () => {
        const response = await api.getRedeemProfile(accessToken, skuId)
        setRedeemProfile(response.data)
      }, true)
    }, [accessToken, skuId, wrapErrorHandler])

    const trackSkuDetail = useCallback(
      async (skuData) => {
        const { id } = skuData
        if (isInited) {
          trackEvent(EVENT_ACTION.VIEW, {
            screen: COUPON_SCREEN_NAME.SKU,
            item_id: id,
            item_type: 'sku',
            item_status: 'available',
          })
        }
      },
      [isInited, trackEvent]
    )

    useEffect(() => {
      if (error) {
        const { code: errCode } = error
        const errorContextOpts = errorUtil.getErrorMessageContext({ errCode })
        handleAjaxError(error, errorContextOpts, true)
      }
    }, [error, handleAjaxError])
    useEffect(() => {
      if (isLoggedIn && hasBindPhone() && skuId) {
        getRedeemProfile()
      }
    }, [isLoggedIn, getRedeemProfile, hasBindPhone, skuId])

    useEffect(() => {
      const isBindPhone = hasBindPhone()
      if (
        !checkingLoginStatus &&
        (!isBindPhone ||
          (isBindPhone &&
            redeemProfile.redeemableStatus !==
              COUPON_REDEEMABLE_STATUS.DEFAULT))
      ) {
        setIsFetchingProfile(false)
      }
    }, [checkingLoginStatus, hasBindPhone, redeemProfile.redeemableStatus])

    useEffect(() => {
      if (sku) {
        const { name, subTitle } = sku
        const sharableLink = permaLinkHashQueryString(
          asPath,
          COUPON_PROJECT.coupon_web,
          'fast'
        )
        setSharablePayload(sharableLink, name || subTitle, name || subTitle)
        trackSkuDetail(sku)
      }
    }, [asPath, pathname, setSharablePayload, sku, trackSkuDetail])

    const closeSnackbar = () => setOpenSanck(false)

    const redeemCoupon = useCallback(async () => {
      setRedeemProfile(
        R.assoc(
          'redeemableStatus',
          COUPON_REDEEMABLE_STATUS.DEFAULT,
          redeemProfile
        )
      )
      trackEvent(EVENT_ACTION.CLICK, {
        screen: COUPON_SCREEN_NAME.SKU,
        button: BUTTON_NAME.REDEEM,
        item_id: sku.id,
        item_type: 'sku',
        item_status: 'available',
      })
      try {
        setIsFetchingProfile(true)
        const response = await api.redeemCoupon(accessToken, skuId)
        setCoupon(response.data)

        // get latest remaining redeem quota
        await getRedeemProfile()
        setIsFetchingProfile(false)
        // TODO: amend this typo:
        setOpenSanck(true)
        // track redeem event
        trackEvent(EVENT_ACTION.RESPONSE, {
          screen: COUPON_SCREEN_NAME.SKU,
          button: BUTTON_NAME.REDEEM,
          result: response.data,
        })
      } catch (e) {
        setIsFetchingProfile(false)
        const isPointsNotRequired = R.propOr(0, 'pointsValue', sku) === 0
        const redeemText = isPointsNotRequired ? '領取' : '兌換'
        const title = isPointsNotRequired ? '領取失敗' : '兌換失敗'
        const retryButtonText = isPointsNotRequired ? '重新領取' : '重新兌換'
        const errCode = R.path(['response', 'data', 'error', 'code'], e)
        const retryRedeem = () => {
          trackEvent(EVENT_ACTION.CLICK, {
            button: BUTTON_NAME.REDEEM_AGAIN,
            screen: COUPON_SCREEN_NAME.SKU,
            item_id: sku.id,
            item_type: 'sku',
            item_status: 'available',
          })
          redeemCoupon()
        }
        const defaultModalContext = {
          title,
          retryButtonText,
          onRetry: retryRedeem,
        }
        const getErrorContext = errorUtil.getErrorMessageContext({
          errCode,
          replaceObj: { redeemText },
          options: defaultModalContext,
          onModalClosed: async () => {
            if (!isFullPage) {
              errorCallback()
            }
            loadingStore.setLoading(true)
            await getRedeemProfile()
            loadingStore.setLoading(false)
          },
        })
        openErrorModalV2(e, getErrorContext)
        // TODO: This need to check if put in here is appropriate
        trackEvent(EVENT_ACTION.RESPONSE, {
          screen: COUPON_SCREEN_NAME.SKU,
          button: BUTTON_NAME.REDEEM,
          result: e.response.data,
          error_type: R.path(['response', 'data', 'error', 'name'], e),
        })
      }
    }, [
      redeemProfile,
      trackEvent,
      sku,
      accessToken,
      skuId,
      getRedeemProfile,
      openErrorModalV2,
      isFullPage,
      loadingStore,
      errorCallback,
    ])

    const clickSnack = useCallback(() => {
      trackEvent(EVENT_ACTION.CLICK, {
        screen: COUPON_SCREEN_NAME.SKU,
        button: BUTTON_NAME.USE_NOW,
        item_id: sku.id,
        item_type: 'sku',
        coupon_id: coupon.id,
        item_status: 'available',
      })
      Router.push(`/coupon/${coupon.id}`)
    }, [coupon, sku, trackEvent])

    const {
      name,
      subTitle,
      summary,
      description,
      amount,
      pointsValue,
      merchant,
      redeemQuota,
      publishStartAt,
      publishEndAt,
      expire,
      detailImageUrl,
      type,
      couponStrategy,
    } = sku || {}

    if (loading) {
      return <DetailLoader showHeader />
    }

    const SkuTabInfo = () => {
      const publishStartAtStr = moment(publishStartAt)
        .locale('zh-hk')
        .format('LL')
      const PublishEndAtStr = moment(publishEndAt).locale('zh-hk').format('LL')

      const expireStr = (() => {
        if (expire.type === 'SPAN') {
          const expireSpan = expire.value
            .replace('d', '日')
            .replace('h', '小時')
          return `自兌換後起${expireSpan}內有效`
        }
        const expireTime = moment(Number(expire.value))
          .locale('zh-hk')
          .format('LLL')
        return `${expireTime} 前有效`
      })()
      const {
        deduction,
        discount,
        minimumCharge,
        maximumDeduction,
      } = couponStrategy
      return (
        <>
          {[COUPON_TYPE.ONLINE_DISCOUNT, COUPON_TYPE.ONLINE_DEDUCTION].includes(
            type
          ) && (
            <>
              {discount && (
                <SkuInfoRow title="折扣額度" content={`${discount / 10} 折`} />
              )}
              {deduction && (
                <SkuInfoRow
                  title="代金券面額"
                  content={formatCashNumber(deduction)}
                />
              )}
              <SkuInfoRow
                title="最低消費金額"
                content={formatCashNumber(minimumCharge)}
              />
              {maximumDeduction && (
                <SkuInfoRow
                  title="最大折扣金額"
                  content={formatCashNumber(maximumDeduction)}
                />
              )}
            </>
          )}
          <SkuInfoRow
            title="換領時段"
            content={`${publishStartAtStr} 至 ${PublishEndAtStr}`}
            showExpire={skuHelper.shouldShowExpiryCountdown({ publishEndAt })}
          />
          <SkuInfoRow title="使用期限" content={expireStr} />
          <SkuInfoRow title="優惠詳情" content={description} />
        </>
      )
    }
    const SkuBaseInfo = () => {
      return (
        <BaseInfo sku={sku}>
          {paymentIntegration && (
            <BuySkuAmount pointsValue={pointsValue} amount={amount} />
          )}
          {isMobileBreakpoint && (
            <FixedWrap>
              <SkuButton
                isLoggedIn={isLoggedIn}
                isFetchingProfile={isFetchingProfile}
                hasBindPhone={hasBindPhone()}
                pointsValue={pointsValue}
                amount={amount}
                redeemQuota={redeemQuota}
                redeemProfile={redeemProfile}
                skuData={sku}
                redeemCoupon={redeemCoupon}
                fastShare={fastShare}
                isMobileBreakpoint={isMobileBreakpoint}
                paymentIntegration={paymentIntegration}
              ></SkuButton>
            </FixedWrap>
          )}
          {!matchesS && (
            <SkuButton
              isLoggedIn={isLoggedIn}
              isFetchingProfile={isFetchingProfile}
              hasBindPhone={hasBindPhone()}
              pointsValue={pointsValue}
              amount={amount}
              redeemQuota={redeemQuota}
              redeemProfile={redeemProfile}
              skuData={sku}
              redeemCoupon={redeemCoupon}
              fastShare={fastShare}
              paymentIntegration={paymentIntegration}
            ></SkuButton>
          )}
        </BaseInfo>
      )
    }
    const takeRedeemText =
      R.propOr(0, 'pointsValue', sku) === 0 ? '領取' : '兌換'
    return (
      <>
        <Head>
          <title>{name}</title>
          <meta property="og:type" content="website" />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:title" content={`${name} | 01優惠券`} />
          <meta
            property="og:description"
            content={summary || subTitle || description}
          />
          <meta property="og:image" content={detailImageUrl} />
          <meta property="og:image:width" content="800" />
          <meta property="og:image:height" content="448" />
        </Head>
        <DetailCard
          sku={sku}
          showHeader
          SkuTabInfo={SkuTabInfo}
          BaseInfo={SkuBaseInfo}
          pageUrl={pageUrl}
          isFullPage={isFullPage}
        >
          <MerchantSkus
            merchantId={merchant.id}
            skuId={skuId}
            isFullPage={isFullPage}
            trackEvent={trackEvent}
          />
        </DetailCard>
        {isMobileBreakpoint || isFullPage ? (
          <FixedWrap>
            <SuccessSnack
              successText={`${takeRedeemText}成功`}
              openSnackbar={openSnack}
              clickSnack={clickSnack}
              closeSnackbar={closeSnackbar}
              vertical={isMobileBreakpoint ? 'bottom' : 'top'}
            />
          </FixedWrap>
        ) : (
          <SuccessSnack
            successText={`${takeRedeemText}成功`}
            openSnackbar={openSnack}
            clickSnack={clickSnack}
            closeSnackbar={closeSnackbar}
            vertical={isMobileBreakpoint ? 'bottom' : 'top'}
          />
        )}
      </>
    )
  }
)

export default SkuDetail
